<template>
    <Head title="Pricing - RentRisk" />

    <!-- Guest / onboarding layout -->
    <GuestLayout v-if="isOnboarding || !isAuthenticated">
        <div class="min-h-screen bg-white py-8 px-4 sm:px-5 lg:px-6">
            <div class="max-w-7xl mx-auto">
                <PricingContent
                    :tiers="tiers"
                    :sections="sections"
                    :stripe-data="stripeData"
                    :payment-method="paymentMethod"
                    :terms-accepted="termsAccepted"
                    :has-payment-method="hasPaymentMethod"
                    :is-onboarding="isOnboarding"
                    :pricing-state="pricingState"
                    :pro-plan="proPlan"
                    :pro-tiers="proTiers"
                />
            </div>
        </div>
    </GuestLayout>

    <!-- Authenticated Layout -->
    <TailwindLayout v-else title="Pricing">
        <div class="max-w-7xl mx-auto">
            <PricingContent
                :tiers="tiers"
                :sections="sections"
                :stripe-data="stripeData"
                :payment-method="paymentMethod"
                :terms-accepted="termsAccepted"
                :has-payment-method="hasPaymentMethod"
                :is-onboarding="isOnboarding"
                :pricing-state="pricingState"
                :pro-plan="proPlan"
                :pro-tiers="proTiers"
            />
        </div>
    </TailwindLayout>
</template>

<script setup>
import { Head, usePage } from '@inertiajs/vue3'
import GuestLayout from '@/Layouts/V2/GuestLayout.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { computed } from 'vue'
import PricingContent from './PricingContent.vue'

const page = usePage()
const isAuthenticated = computed(() => !!page.props.auth?.user)

// Get tiers from controller (passed as props)
// Ensure tiers is always an array to prevent frontend errors
const tiers = computed(() => {
    const tiersData = page.props.tiers
    return Array.isArray(tiersData) ? tiersData : []
})

// Get subscription-related props from controller
const stripeData = computed(() => page.props.stripeData || null)
const paymentMethod = computed(() => page.props.paymentMethod || null)
const termsAccepted = computed(() => page.props.termsAccepted || false)
const hasPaymentMethod = computed(() => page.props.hasPaymentMethod || false)
// 86bcd9hg3: account-wide Pro — the landlord's pricing state and quote, and the tier table.
const pricingState = computed(() => page.props.pricingState || 'basic')
const proPlan = computed(() => page.props.proPlan || null)
const proTiers = computed(() => (Array.isArray(page.props.proTiers) ? page.props.proTiers : []))
// 86bc2cgxd: straight after signup, force the public-facing onboarding
// layout (GuestLayout) even though the user is auto-logged-in. Standalone
// /pricing visits from the dashboard keep the authenticated TailwindLayout.
const isOnboarding = computed(() => page.props.isOnboarding === true)

const autopayEnabled = computed(() => page.props.autopay_enabled === true)

// Feature rows come from the admin-managed pricing_feature_* tables (RAC-10291),
// emitted by PricingController as the `sections` prop. Each feature's `tiers` map
// is keyed by each plan's STABLE base slug (planSlug: 'basic', 'protect-pro'), not the mutable DB display name — keying on the name meant
// renaming a plan (e.g. "RentRisk PROTECT" -> "RiskGuard") silently blanked every
// checkmark in that column. See tierKey() in PricingContent.vue.
const baseSections = computed(() => {
    const data = page.props.sections
    return Array.isArray(data) ? data : []
})

// Hide the Tenant Autopay feature row while autopay is disabled (AUTOPAY_ENABLED flag).
const sections = computed(() => baseSections.value.map((section) => ({
    ...section,
    features: section.features.filter((feature) => autopayEnabled.value || feature.name !== 'Tenant Autopay'),
})))
</script>

